import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";

interface AuthCardProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

/**
 * Auth Card Component
 *
 * Card wrapper for authentication forms with consistent styling.
 * Matches the landing page aesthetic.
 */
export function AuthCard({ title, description, children, footer, className }: AuthCardProps) {
  return (
    <div className={cn("mx-auto w-full max-w-md", className)}>
      {/* Logo */}
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-2">
          <img
            src="/logo.svg"
            alt="Logo"
            className="h-8 w-8"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <span className="text-xl font-bold text-foreground">Homeloupe</span>
        </Link>
      </div>

      <Card className="border-border bg-card shadow-sm">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-center text-2xl font-bold tracking-tight">{title}</CardTitle>
          {description && (
            <CardDescription className="text-center text-muted-foreground">
              {description}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>

      {/* Footer links */}
      {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
    </div>
  );
}

export default AuthCard;
